import React, { useEffect, useState } from "react";
import axios from "axios";
import { Download, Calendar, FileText, Receipt, TrendingUp } from "lucide-react";
import { PageHeader, Spinner } from "../components/Brand";
import { useApp, API } from "../AppContext";

const fmt = (n) => new Intl.NumberFormat("fr-CA", { style: "currency", currency: "CAD" }).format(n || 0);

export default function Reports() {
  const { lang } = useApp();
  const today = new Date().toISOString().slice(0, 10);
  const yearStart = `${new Date().getFullYear()}-01-01`;

  const [scope, setScope] = useState("invoices");
  const [start, setStart] = useState(yearStart);
  const [end, setEnd] = useState(today);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const token = localStorage.getItem("progere_token");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/reports/tax`, { params: { scope, start: `${start}T00:00:00Z`, end: `${end}T23:59:59Z` } });
      setData(data);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [scope, start, end]);

  const downloadCsv = async () => {
    const res = await fetch(`${API}/reports/tax/csv?scope=${scope}&start=${start}T00:00:00Z&end=${end}T23:59:59Z`, { headers: { Authorization: `Bearer ${token}` } });
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `tps-tvq-${start}_${end}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const PRESETS = [
    { id: "q1", label_fr: "T1 (Jan-Mar)", label_en: "Q1", start: `${new Date().getFullYear()}-01-01`, end: `${new Date().getFullYear()}-03-31` },
    { id: "q2", label_fr: "T2 (Avr-Juin)", label_en: "Q2", start: `${new Date().getFullYear()}-04-01`, end: `${new Date().getFullYear()}-06-30` },
    { id: "q3", label_fr: "T3 (Juil-Sep)", label_en: "Q3", start: `${new Date().getFullYear()}-07-01`, end: `${new Date().getFullYear()}-09-30` },
    { id: "q4", label_fr: "T4 (Oct-Déc)", label_en: "Q4", start: `${new Date().getFullYear()}-10-01`, end: `${new Date().getFullYear()}-12-31` },
    { id: "year", label_fr: "Année courante", label_en: "Current year", start: yearStart, end: today },
  ];

  const T = lang === "fr" ? {
    title: "Rapport fiscal TPS/TVQ",
    sub: "Pour votre déclaration trimestrielle ou annuelle. Conforme aux exigences de Revenu Québec.",
    period: "Période",
    from: "Du",
    to: "Au",
    scope_invoices: "Factures (montants encaissés)",
    scope_quotes: "Soumissions acceptées",
    subtotal: "Sous-total HT",
    gst: "TPS perçue (5%)",
    qst: "TVQ perçue (9,975%)",
    grand: "Total TTC",
    docs: "documents",
    download: "Télécharger le CSV",
    no_data: "Aucun document dans cette période.",
    list: "Détail des documents",
    number: "Numéro", date: "Date", client: "Client",
    note: "💡 Astuce : transmettez ce CSV directement à votre comptable pour la déclaration TPS/TVQ. Format compatible Excel et Google Sheets.",
  } : {
    title: "GST/QST tax report",
    sub: "For your quarterly or annual filing. Compliant with Revenu Québec requirements.",
    period: "Period",
    from: "From",
    to: "To",
    scope_invoices: "Invoices (collected)",
    scope_quotes: "Approved quotes",
    subtotal: "Subtotal (excl. tax)",
    gst: "GST collected (5%)",
    qst: "QST collected (9.975%)",
    grand: "Grand total",
    docs: "documents",
    download: "Download CSV",
    no_data: "No documents in this period.",
    list: "Document details",
    number: "Number", date: "Date", client: "Client",
    note: "💡 Tip: hand this CSV directly to your accountant for filing. Compatible with Excel and Google Sheets.",
  };

  return (
    <div data-testid="reports-page">
      <PageHeader title={T.title} subtitle={T.sub}
        action={data && data.count > 0 && (
          <button onClick={downloadCsv} className="btn-red text-sm" data-testid="reports-download-csv">
            <Download size={14} /> {T.download}
          </button>
        )} />

      {/* Filters */}
      <div className="glass-card p-5 mb-6 max-w-5xl" data-testid="reports-filters">
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 mb-4">
          <div>
            <label className="text-xs uppercase tracking-wider" style={{ color: "rgba(245,245,245,0.5)" }}>{T.from}</label>
            <input type="date" className="input-dark mt-1" value={start} onChange={(e) => setStart(e.target.value)} data-testid="reports-start" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider" style={{ color: "rgba(245,245,245,0.5)" }}>{T.to}</label>
            <input type="date" className="input-dark mt-1" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="reports-end" />
          </div>
          <div className="sm:col-span-2">
            <label className="text-xs uppercase tracking-wider" style={{ color: "rgba(245,245,245,0.5)" }}>{lang === "fr" ? "Source" : "Source"}</label>
            <select className="input-dark mt-1" value={scope} onChange={(e) => setScope(e.target.value)} data-testid="reports-scope">
              <option value="invoices">{T.scope_invoices}</option>
              <option value="quotes">{T.scope_quotes}</option>
            </select>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {PRESETS.map((p) => (
            <button key={p.id} onClick={() => { setStart(p.start); setEnd(p.end); }}
              className="px-3 py-1.5 text-xs rounded-full transition"
              style={{ background: "rgba(212,175,55,0.06)", color: "#D4AF37", border: "1px solid rgba(212,175,55,0.25)" }}
              data-testid={`reports-preset-${p.id}`}>
              <Calendar size={10} className="inline mr-1" /> {lang === "fr" ? p.label_fr : p.label_en}
            </button>
          ))}
        </div>
      </div>

      {loading || !data ? <Spinner /> : (
        <>
          {/* KPI tiles */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6 max-w-5xl">
            <KpiTile label={T.subtotal} value={fmt(data.subtotal)} color="#F5F5F5" icon={FileText} testid="kpi-subtotal" />
            <KpiTile label={T.gst} value={fmt(data.gst_total)} color="#D4AF37" icon={Receipt} testid="kpi-gst" />
            <KpiTile label={T.qst} value={fmt(data.qst_total)} color="#D4AF37" icon={Receipt} testid="kpi-qst" />
            <KpiTile label={T.grand} value={fmt(data.total)} color="#4ADE80" icon={TrendingUp} testid="kpi-grand" />
          </div>

          <p className="text-xs mb-6 max-w-5xl" style={{ color: "rgba(245,245,245,0.55)" }}>
            <strong>{data.count}</strong> {T.docs} {lang === "fr" ? "trouvés du" : "found from"} <strong style={{ color: "#D4AF37" }}>{start}</strong> {lang === "fr" ? "au" : "to"} <strong style={{ color: "#D4AF37" }}>{end}</strong>
          </p>

          {/* List */}
          {data.rows.length === 0 ? (
            <div className="glass-card p-10 text-center max-w-5xl" data-testid="reports-empty">
              <p style={{ color: "rgba(245,245,245,0.6)" }}>{T.no_data}</p>
            </div>
          ) : (
            <div className="glass-card p-6 max-w-5xl">
              <h3 className="font-heading text-lg font-bold mb-4" style={{ color: "#D4AF37" }}>{T.list}</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-xs uppercase tracking-wider" style={{ color: "rgba(245,245,245,0.5)" }}>
                      <th className="text-left py-2 px-2">{T.number}</th>
                      <th className="text-left py-2 px-2">{T.date}</th>
                      <th className="text-left py-2 px-2">{T.client}</th>
                      <th className="text-right py-2 px-2">{T.subtotal}</th>
                      <th className="text-right py-2 px-2">TPS</th>
                      <th className="text-right py-2 px-2">TVQ</th>
                      <th className="text-right py-2 px-2">{T.grand}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.rows.map((r) => (
                      <tr key={r.id} className="border-t border-white/5">
                        <td className="py-2 px-2 font-mono text-xs" style={{ color: "#D4AF37" }}>{r.number}</td>
                        <td className="py-2 px-2" style={{ color: "rgba(245,245,245,0.7)" }}>{r.date.slice(0, 10)}</td>
                        <td className="py-2 px-2" style={{ color: "#F5F5F5" }}>{r.client_name}</td>
                        <td className="py-2 px-2 text-right" style={{ color: "rgba(245,245,245,0.78)" }}>{fmt(r.subtotal)}</td>
                        <td className="py-2 px-2 text-right" style={{ color: "rgba(245,245,245,0.7)" }}>{fmt(r.gst)}</td>
                        <td className="py-2 px-2 text-right" style={{ color: "rgba(245,245,245,0.7)" }}>{fmt(r.qst)}</td>
                        <td className="py-2 px-2 text-right font-semibold" style={{ color: "#F5F5F5" }}>{fmt(r.total)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="border-t-2 border-[#D4AF37]/30">
                      <td colSpan={3} className="py-3 px-2 font-bold uppercase tracking-wider text-xs" style={{ color: "#D4AF37" }}>TOTAUX</td>
                      <td className="py-3 px-2 text-right font-bold" style={{ color: "#F5F5F5" }}>{fmt(data.subtotal)}</td>
                      <td className="py-3 px-2 text-right font-bold" style={{ color: "#D4AF37" }}>{fmt(data.gst_total)}</td>
                      <td className="py-3 px-2 text-right font-bold" style={{ color: "#D4AF37" }}>{fmt(data.qst_total)}</td>
                      <td className="py-3 px-2 text-right font-heading text-lg font-bold" style={{ color: "#4ADE80" }}>{fmt(data.total)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          )}

          <p className="text-xs mt-6 max-w-5xl p-4 rounded-xl" style={{ background: "rgba(212,175,55,0.05)", border: "1px solid rgba(212,175,55,0.15)", color: "rgba(245,245,245,0.75)" }}>
            {T.note}
          </p>
        </>
      )}
    </div>
  );
}

function KpiTile({ icon: Icon, color, label, value, testid }) {
  return (
    <div className="glass-card p-5" data-testid={testid}>
      <div className="flex items-center gap-2 mb-2">
        <Icon size={14} color={color} />
        <div className="text-xs uppercase tracking-wider" style={{ color: "rgba(245,245,245,0.55)" }}>{label}</div>
      </div>
      <div className="font-heading text-xl sm:text-2xl font-black" style={{ color }}>{value}</div>
    </div>
  );
}
